// src/components/legal/ArrowQuestTermsOfService.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.8;
    color: #333;
`;

const Header = styled.header`
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #eee;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #2E86EE;
`;

const UpdateDate = styled.p`
    color: #666;
    font-size: 1rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: #2E86EE;
    text-decoration: none;
    margin-bottom: 2rem;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &::before {
        content: '←';
        margin-right: 0.5rem;
    }
`;

const Section = styled.section`
    margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #444;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
    text-align: justify;
    white-space: pre-wrap;
`;

const WarningBox = styled.div`
    background: #FFF3E0;
    border-left: 4px solid #FF9800;
    padding: 1.5rem;
    border-radius: 5px;
    margin: 2rem 0;
`;

const InfoBox = styled.div`
    background: #E8F1FC;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    white-space: pre-wrap;
`;

const ArrowQuestTermsOfService = () => {
    return (
        <Container>
            <BackLink to="/arrowquest">Arrow Quest로 돌아가기</BackLink>

            <Header>
                <Title>Arrow Quest Terms of Service / 이용약관</Title>
                <UpdateDate>Last updated / 최종 수정일: May 26, 2026</UpdateDate>
            </Header>

            <WarningBox>
                <strong>Notice / 안내:</strong> Arrow Quest is a casual mobile puzzle game intended for entertainment. By installing or using the game, you agree to these Terms. If you do not agree, please uninstall the game and do not use the service.
                <br /><br />
                Arrow Quest는 오락 목적의 캐주얼 모바일 퍼즐 게임입니다. 본 약관에 동의하지 않으시면 게임을 이용하실 수 없습니다.
            </WarningBox>

            <Section>
                <SectionTitle>1. Acceptance of Terms / 약관 동의</SectionTitle>
                <Paragraph>
                    These Terms of Service ("Terms") govern your access to and use of the Arrow Quest mobile game ("Service") provided by MindWorld ("Company", "we", "us"). By installing, accessing, or using the Service, you confirm that:

                    • You have read, understood, and agreed to these Terms and our Privacy Policy.
                    • You are at least 13 years of age (16 in EEA jurisdictions that apply the higher age threshold under GDPR Art. 8).
                    • You have the legal capacity to enter into a binding agreement, or have obtained consent from a parent or legal guardian.
                    • You will comply with all applicable laws and regulations.

                    본 약관에 동의함으로써 만 13세 이상(EU 일부 국가는 16세)이며 법적 계약 체결 능력이 있음을 확인합니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>2. The Service / 서비스 설명</SectionTitle>
                <Paragraph>
                    Arrow Quest is a free-to-play casual puzzle game offered on Android via Google Play. The game includes:

                    • Puzzle levels with progressive difficulty
                    • In-game currency (coins) and consumable boosters
                    • Optional in-app purchases (real-money transactions)
                    • Advertisements (banner, interstitial, rewarded, app open) supplied by third-party ad networks
                    • Local save of game progress on your device
                    • Optional cloud save via Firebase Authentication and Cloud Firestore (Google Cloud)
                    • Optional global and country leaderboards (stage progression + daily challenge records)

                    The Service is provided "as is" and may change, expand, or be discontinued at any time.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>3. License Grant / 라이선스 부여</SectionTitle>
                <Paragraph>
                    Subject to your compliance with these Terms, we grant you a personal, non-exclusive, non-transferable, revocable, limited license to install and use Arrow Quest on a compatible device that you own or control, solely for your personal, non-commercial entertainment.

                    All rights not expressly granted to you are reserved by us.

                    본 약관 준수 조건 하에 개인적, 비상업적 용도로만 게임을 이용할 수 있는 제한된 라이선스를 부여합니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>4. User Conduct / 이용자 의무</SectionTitle>
                <Paragraph>
                    You agree NOT to:

                    • Reverse engineer, decompile, disassemble, or attempt to derive source code
                    • Modify, hack, cheat, or exploit the game (including memory editors, automation, bots, modded clients)
                    • Circumvent advertising or in-app purchase mechanisms
                    • Submit, edit, or attempt to submit falsified, automated, or otherwise manipulated leaderboard scores (stages, daily challenges)
                    • Tamper with cloud save data to gain progress, currency, or items you did not legitimately earn
                    • Impersonate another player or use a display name that is offensive, infringing, misleading, or otherwise inappropriate
                    • Use the Service for any unlawful, fraudulent, or harmful purpose
                    • Distribute malware, viruses, or any code that interferes with the Service
                    • Interfere with or disrupt the Service or servers
                    • Resell, rent, or sublicense the Service or any part of it
                    • Use automated means to access the Service
                    • Misrepresent your identity or affiliation
                    • Violate intellectual property rights of others

                    Violations may result in termination of your access without refund and removal of associated leaderboard entries and cloud-save documents.

                    위반 시 사전 통지 없이 서비스 이용 제한 및 랭킹·클라우드 세이브 데이터 삭제가 이뤄질 수 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>5. In-App Purchases / 인앱 구매</SectionTitle>
                <Paragraph>
                    Arrow Quest offers optional in-app purchases (e.g., coin packs, ad removal). All purchases are processed by Google Play Billing under Google's terms of service. Once delivered, virtual items are non-refundable except as required by applicable law.

                    Pricing: Displayed in your local currency, inclusive of applicable taxes. We may change prices with prospective effect.

                    Virtual items: Have no monetary value, cannot be exchanged for cash, and exist only within the game. They may be discontinued without compensation if the Service ends.

                    Refunds: Subject to Google Play's refund policy. Korean users have additional rights under the Act on Consumer Protection in Electronic Commerce (전자상거래법 제17조) — you may request a refund within 7 days of purchase if the digital content has not been used. For EU users, the statutory 14-day right of withdrawal applies, but is waived for digital content delivered immediately upon your consent (consent obtained at purchase).

                    Contact mind-wellness@naver.com for refund disputes.

                    환불: Google Play 정책에 따르며, 한국 전자상거래법 및 EU 소비자권리지침에 따른 권리도 적용됩니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>6. Advertisements / 광고</SectionTitle>
                <Paragraph>
                    The free version of Arrow Quest is supported by advertisements served via Google AdMob and its mediation partners. By using the free version, you acknowledge that ads will be displayed.

                    Ad personalization is governed by your consent (in EEA/UK/Switzerland) or your device's advertising preferences (in other regions). Removing ads is available as an in-app purchase.

                    We are not responsible for the content of third-party advertisements or websites linked from them. Always exercise discretion before interacting with advertisements.

                    광고: 무료 버전은 광고로 운영되며, 광고 제거는 인앱 구매로 가능합니다. 광고 콘텐츠는 제3자가 제공하므로 회사는 그에 대해 책임지지 않습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>7. Cloud Save, Account, and Leaderboards / 클라우드 세이브·계정·랭킹</SectionTitle>
                <Paragraph>
                    Account: On first launch, Firebase Authentication automatically issues an anonymous identifier ("Firebase UID") used to bootstrap optional cloud features. You may, at your option, link this anonymous identity to a Google account via in-game Settings → "Sign in with Google". Linking only exchanges the OAuth credential issued by Google Play Games (Android); the Company receives your Google display name only. You are responsible for safeguarding access to the Google account you choose to link.

                    Cloud save: When enabled, a JSON snapshot of your game progress is stored at Firestore path users/[your-uid]. Cloud save is a convenience feature, not a guarantee — saves are subject to occasional service outages, schema migrations, and corrective rollbacks. You should treat your local save as the primary copy where possible.

                    Display name: The display name shown on leaderboards is taken from your Google account ("Google Player Name" on Android). You are responsible for the content of your display name. We may, at our sole discretion and without prior notice, replace or anonymize display names that are abusive, infringing, deceptive, or otherwise violate these Terms.

                    Country code: Your leaderboard country is automatically detected from your device locale (ISO 2-letter region). "ZZ" is used when detection fails. You acknowledge that country-segregated leaderboards rely on this self-declared signal.

                    Fair play: Leaderboard entries are gated by server-side rules (stage score may not regress; daily-challenge updates require strictly better moves/time). Attempts to bypass these rules, including submitting impossible scores, will result in removal of your entries and may result in termination of cloud access. We may reset or wipe leaderboards (in whole or by region) where necessary to remediate exploitation.

                    Service changes: The Company may modify, suspend, or discontinue cloud save and leaderboards at any time. Where reasonably practicable, we will provide in-app notice before permanent shutdown of these features and a period to export or back up your save.

                    데이터 처리: 클라우드 세이브·랭킹에서 수집·전송·보유되는 데이터의 자세한 내역은 개인정보처리방침을 참조하세요.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>8. Intellectual Property / 지적재산권</SectionTitle>
                <Paragraph>
                    All content in Arrow Quest, including but not limited to graphics, sound, code, logos, level designs, and the "Arrow Quest" name, is the property of MindWorld or its licensors, and is protected by copyright, trademark, and other intellectual property laws.

                    You may not copy, modify, distribute, sell, or create derivative works of the Service without our prior written consent.

                    게임의 모든 콘텐츠는 회사 또는 라이선스 제공자의 자산이며, 무단 복제·배포·수정은 금지됩니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>9. Third-Party Services / 제3자 서비스</SectionTitle>
                <Paragraph>
                    The Service integrates with third-party services, including:

                    • Google Play Services (account, billing)
                    • Google Play Games Services (Google account linking on Android)
                    • Firebase Authentication and Cloud Firestore (Google Cloud) — anonymous account and optional cloud save / leaderboards
                    • Google AdMob (advertising)
                    • Google User Messaging Platform (consent management)
                    • DOTween, TextMeshPro and other open-source libraries (under their respective licenses)

                    Your use of these third-party services is governed by their own terms and privacy policies. We are not responsible for third-party services.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>10. Disclaimers / 면책조항</SectionTitle>
                <Paragraph>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR UNINTERRUPTED OPERATION.

                    We do not warrant that the Service will be error-free, secure, or available at all times. Use of the Service is at your own risk.

                    Nothing in these Terms excludes or limits liability that cannot be excluded under applicable law (e.g., for death or personal injury caused by negligence, or fraud).

                    법이 허용하는 최대 범위 내에서 서비스는 "있는 그대로" 제공되며, 명시적·묵시적 어떠한 보증도 하지 않습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>11. Limitation of Liability / 책임 제한</SectionTitle>
                <Paragraph>
                    To the maximum extent permitted by applicable law, in no event will MindWorld be liable for:

                    • Any indirect, incidental, special, consequential, exemplary, or punitive damages
                    • Loss of profits, data, goodwill, or business opportunity
                    • Damages exceeding the greater of (a) amounts you paid to us in the 12 months preceding the claim, or (b) USD 50

                    Some jurisdictions do not allow exclusion of certain warranties or limitation of liability for consequential or incidental damages, so the above may not fully apply to you. For consumers in the EEA, mandatory consumer protections are unaffected.

                    책임 한도: 법이 허용하는 최대 범위 내에서 책임이 제한되며, EU/한국 소비자 보호 강행규정은 영향받지 않습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>12. Indemnification / 손해배상</SectionTitle>
                <Paragraph>
                    You agree to indemnify and hold harmless MindWorld and its affiliates, officers, agents, and employees from any claim, demand, loss, or expense (including reasonable attorneys' fees) arising out of:

                    • Your use of the Service
                    • Your breach of these Terms
                    • Your violation of any law or rights of a third party

                    This obligation will survive termination of these Terms.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>13. Termination / 서비스 해지</SectionTitle>
                <Paragraph>
                    By you: Uninstall the game at any time. You may also request deletion of any data we hold about you via the Account Deletion page.

                    By us: We may suspend or terminate your access immediately, without prior notice or liability, for any breach of these Terms, fraud, or where required by law. We may also discontinue the Service in whole or in part, with reasonable notice where practicable.

                    Effects of termination: Your right to use the Service ceases. Sections relating to intellectual property, disclaimers, limitation of liability, indemnification, and governing law survive termination.

                    해지: 사용자는 언제든 게임을 삭제할 수 있으며, 회사는 약관 위반 시 사전 통지 없이 서비스 이용을 제한할 수 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>14. Governing Law and Disputes / 준거법 및 분쟁 해결</SectionTitle>
                <Paragraph>
                    Governing law: These Terms are governed by the laws of the Republic of Korea, without regard to its conflict of law principles.

                    Consumer protections: If you are a consumer residing in the EEA, UK, or another jurisdiction with mandatory consumer protection laws, you also benefit from the mandatory provisions of the law of your country of residence.

                    Dispute resolution:
                    1. Informal resolution: contact mind-wellness@naver.com first.
                    2. Mediation: Korean Consumer Agency (소비자분쟁조정위원회) for Korean consumers; ODR platform (https://ec.europa.eu/consumers/odr) for EU consumers.
                    3. Litigation: Seoul Central District Court, Republic of Korea, has exclusive jurisdiction unless mandatory consumer law provides otherwise.

                    준거법: 대한민국 법률. EU/영국 소비자는 거주국 강행규정의 보호를 받습니다. 한국 소비자는 소비자분쟁조정위원회 조정 절차 이용 가능.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>15. Changes to These Terms / 약관 변경</SectionTitle>
                <Paragraph>
                    We may modify these Terms from time to time. Material changes will be announced in-app at least 30 days before they take effect (7 days for non-material changes). Your continued use of the Service after the effective date constitutes acceptance of the modified Terms. If you do not agree, please uninstall the Service.

                    중요 약관 변경은 시행 30일 전 사전 고지하며, 사용자가 동의하지 않을 경우 서비스 이용을 중단할 수 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>16. Miscellaneous / 기타</SectionTitle>
                <Paragraph>
                    Entire agreement: These Terms together with the Privacy Policy constitute the entire agreement between you and us.

                    Severability: If any provision is held invalid, the remaining provisions remain in full force.

                    No waiver: Failure to enforce any right is not a waiver of that right.

                    Assignment: You may not assign your rights without our consent. We may assign these Terms in connection with a merger, acquisition, or sale of assets.

                    Language: These Terms are provided in English and Korean. In case of conflict, the Korean version prevails for users in Korea; the English version prevails for all other users.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>17. Contact / 연락처</SectionTitle>
                <Paragraph>
                    Company / 회사:
                    • Name: MindWorld (마인드월드)
                    • Representative: 윤우재
                    • Address: 경기도 의왕시 시청1로 11, Republic of Korea
                    • Business Registration: 504-43-28680
                    • Email: mind-wellness@naver.com

                    Response time: We aim to respond within 48 business hours.
                </Paragraph>
            </Section>

            <InfoBox>
                <SectionTitle>Document Information / 문서 정보</SectionTitle>
                <Paragraph>
                    • Originally published / 최초 작성일: 2026-05-18
                    • Last updated / 최종 수정일: 2026-05-26
                    • Effective / 시행일: 2026-05-26
                    • Version / 버전: 1.1
                    • v1.1 changes / 1.1 변경사항: Firebase Authentication 기반 계정·클라우드 세이브·랭킹 관련 약관(§7) 신설 및 후속 조항 번호 조정, 부정 점수 제출 등 금지행위(§4) 보강.
                </Paragraph>
            </InfoBox>
        </Container>
    );
};

export default ArrowQuestTermsOfService;
